import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import AuditEventTable from '../../components/audit/AuditEventTable';
import { getSuperAdminAuthEvents, listCompanies } from '../../lib/api';
import { toast } from 'sonner';

// ─── Constants ────────────────────────────────────────────────────────────────

const AUDIT_EVENT_TYPES = [
    'login_success',
    'login_fail',
    'login_locked',
    'password_change',
    'role_change',
    'account_deleted',
    'lead.submitted',
    'impersonation_start',
    'company_deleted',
];

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function SuperAdminAuditLogPage() {
    const { t } = useTranslation();

    const [events, setEvents] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [companiesLoading, setCompaniesLoading] = useState(true);

    const [companyFilter, setCompanyFilter] = useState('');
    const [eventTypeFilter, setEventTypeFilter] = useState('');

    // Detect whether any event in the current dataset uses the pre-tenant sentinel
    const hasPreTenantEvents = useMemo(
        () => events.some((e) => e.company_id === 'none'),
        [events]
    );

    // ── Fetch companies for filter dropdown ───────────────────────────────────
    useEffect(() => {
        setCompaniesLoading(true);
        listCompanies()
            .then((res) => setCompanies(res.data ?? []))
            .catch(() => {})
            .finally(() => setCompaniesLoading(false));
    }, []);

    // ── Fetch audit events ────────────────────────────────────────────────────
    const fetchEvents = useCallback(
        async (companyId, eventType) => {
            setLoading(true);
            try {
                const params = {};
                if (companyId) params.company_id = companyId;
                if (eventType) params.event_type = eventType;
                const res = await getSuperAdminAuthEvents(params);
                setEvents(res.data ?? []);
            } catch {
                toast.error(t('superadmin.load_error'));
            } finally {
                setLoading(false);
            }
        },
        [t]
    );

    useEffect(() => {
        fetchEvents('', '');
    }, [fetchEvents]);

    // ── Filter handlers ───────────────────────────────────────────────────────
    const handleCompanyChange = (value) => {
        const next = value === 'all' ? '' : value;
        setCompanyFilter(next);
        fetchEvents(next, eventTypeFilter);
    };

    const handleEventTypeChange = (value) => {
        const next = value === 'all' ? '' : value;
        setEventTypeFilter(next);
        fetchEvents(companyFilter, next);
    };

    const handleRefresh = () => {
        fetchEvents(companyFilter, eventTypeFilter);
    };

    // ─────────────────────────────────────────────────────────────────────────

    return (
        <div className="space-y-6" data-testid="superadmin-audit-log-page">
            {/* Page header */}
            <div>
                <h1 className="text-2xl font-semibold text-gray-900">
                    {t('superadmin.audit_log_title')}
                </h1>
                <p className="text-sm text-gray-500 mt-1">
                    {t('superadmin.audit_log_intro')}
                </p>
            </div>

            {/* Toolbar */}
            <div className="flex items-center gap-3 flex-wrap">
                {/* Company filter */}
                <Select
                    value={companyFilter || 'all'}
                    onValueChange={handleCompanyChange}
                    disabled={companiesLoading}
                >
                    <SelectTrigger className="w-56" data-testid="audit-company-filter">
                        <SelectValue placeholder={t('audit.filter.all_companies')} />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">{t('audit.filter.all_companies')}</SelectItem>
                        {companies.map((c) => (
                            <SelectItem key={c.slug} value={c.slug}>
                                {c.name}
                            </SelectItem>
                        ))}
                        {/* Pre-tenant sentinel — only shown if events contain it */}
                        {hasPreTenantEvents && (
                            <SelectItem value="none">
                                {t('audit.filter.pre_tenant')}
                            </SelectItem>
                        )}
                    </SelectContent>
                </Select>

                {/* Event type filter */}
                <Select
                    value={eventTypeFilter || 'all'}
                    onValueChange={handleEventTypeChange}
                >
                    <SelectTrigger className="w-52" data-testid="audit-event-type-filter">
                        <SelectValue placeholder={t('audit.filter.all_events')} />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">{t('audit.filter.all_events')}</SelectItem>
                        {AUDIT_EVENT_TYPES.map((et) => (
                            <SelectItem key={et} value={et}>
                                {t(`audit.event.${et}`, { defaultValue: et })}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>

                <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRefresh}
                    disabled={loading}
                    data-testid="audit-refresh-btn"
                >
                    <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
                    {t('audit.refresh')}
                </Button>
            </div>

            {/* Table */}
            <AuditEventTable
                events={events}
                includeCompanyColumn
                loading={loading}
                t={t}
            />

            {/* Limit note */}
            <p className="text-xs text-gray-400">
                {t('superadmin.audit_log_limit_note')}
            </p>
        </div>
    );
}
